import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Paper,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CorrectIcon,
  Cancel as WrongIcon,
  Share as ShareIcon,
  Refresh as RetryIcon,
  Home as HomeIcon,
  Timer as TimerIcon,
  Score as ScoreIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuiz } from '../../contexts/QuizContext';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import ContentRenderer from '../common/ContentRenderer';

interface QuizResultsProps {
  quizId?: number;
  attemptId?: number;
}

const QuizResults: React.FC<QuizResultsProps> = ({
  quizId: propQuizId,
  attemptId: propAttemptId
}) => {
  const navigate = useNavigate();
  const { quizId: paramQuizId, attemptId: paramAttemptId } = useParams<{
    quizId: string;
    attemptId: string;
  }>();

  const quizId = propQuizId || (paramQuizId ? parseInt(paramQuizId) : null);
  const attemptId = propAttemptId || (paramAttemptId ? parseInt(paramAttemptId) : null);

  const {
    quizResults,
    takingLoading,
    takingError,
    fetchQuizResults,
    clearTakingState,
  } = useQuiz();

  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    if (attemptId && !quizResults) {
      fetchQuizResults(attemptId);
    }
  }, [attemptId, fetchQuizResults, quizResults]);

  const handleShare = async () => {
    if (!quizResults || !quizId) return;

    const shareUrl = `${window.location.origin}/quiz/${quizId}`;
    const shareText = `Tôi vừa hoàn thành bài tập "${quizResults.quiz?.title || 'Bài tập trắc nghiệm'}" với điểm số ${(Number(quizResults.attempt?.percentage) || 0).toFixed(1)}%! Hãy thử sức với bài tập này nhé: ${shareUrl}`;

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage('Đã sao chép link chia sẻ!');
      setTimeout(() => setShareMessage(''), 3000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setShareMessage('Không thể sao chép link');
      setTimeout(() => setShareMessage(''), 3000);
    }
  };

  const handleRetry = () => {
    if (!quizId) return;
    clearTakingState();
    navigate(`/quiz/${quizId}/take`);
  };

  const getScoreColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (percentage: number): string => {
    if (percentage >= 90) return 'Xuất sắc';
    if (percentage >= 80) return 'Giỏi';
    if (percentage >= 70) return 'Khá';
    if (percentage >= 60) return 'Trung bình';
    if (percentage >= 50) return 'Yếu';
    return 'Kém';
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Loading state
  if (takingLoading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <ScoreIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Đang tải kết quả...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  // Error state
  if (takingError) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {takingError}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/quizzes')}
          >
            Quay lại danh sách bài tập
          </Button>
        </Box>
      </Container>
    );
  }

  // Results not loaded or incomplete
  if (!quizResults || !quizResults.quiz || !quizResults.attempt) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <ScoreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Không tìm thấy kết quả
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/quizzes')}
            >
              Quay lại danh sách bài tập
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  const { quiz, attempt, questions } = quizResults;
  const correctCount = questions?.filter(q => q.is_correct).length || 0;
  const totalQuestions = questions?.length || 0;
  const percentage = Number(attempt?.percentage) || 0;
  const scoreColor = getScoreColor(percentage);
  const scoreLabel = getScoreLabel(percentage);

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {/* Header */}
        <Paper sx={{ p: 4, mb: 3, textAlign: 'center' }}>
          <ScoreIcon
            sx={{
              fontSize: 80,
              color: `${scoreColor}.main`,
              mb: 2
            }}
          />

          <Typography variant="h4" gutterBottom>
            Kết quả bài tập
          </Typography>

          <Typography variant="h5" color="text.secondary" gutterBottom>
            {quiz?.title || 'Bài tập trắc nghiệm'}
          </Typography>

          {shareMessage && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              {shareMessage}
            </Alert>
          )}

          {/* Score Display */}
          <Box sx={{ my: 4 }}>
            <Typography variant="h2" color={`${scoreColor}.main`} gutterBottom>
              {percentage.toFixed(1)}%
            </Typography>

            <Chip
              label={scoreLabel}
              color={scoreColor}
              sx={{ fontSize: '1.1rem', px: 2, py: 1 }}
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {correctCount} / {totalQuestions} câu đúng
            </Typography>

            <Typography variant="body1" color="text.secondary">
              Điểm số: {Number(attempt?.score) || 0} / {Number(attempt?.total_score) || 0}
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              color={scoreColor}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: 'grey.200',
              }}
            />
          </Box>
        </Paper>

        {/* Statistics */}
        <Box display="flex" gap={2} mb={3}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimerIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Thời gian làm bài
              </Typography>
              <Typography variant="h5" color="primary">
                {formatTime(Number(attempt?.time_taken) || 0)}
              </Typography>
              {quiz?.time_limit && (
                <Typography variant="body2" color="text.secondary">
                  Giới hạn: {quiz?.time_limit} phút
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CorrectIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Câu trả lời đúng
              </Typography>
              <Typography variant="h5" color="success.main">
                {correctCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tỷ lệ: {totalQuestions > 0 ? ((correctCount / totalQuestions) * 100).toFixed(1) : '0.0'}%
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <WrongIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Câu trả lời sai
              </Typography>
              <Typography variant="h5" color="error.main">
                {totalQuestions - correctCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tỷ lệ: {totalQuestions > 0 ? (((totalQuestions - correctCount) / totalQuestions) * 100).toFixed(1) : '0.0'}%
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Attempt Info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Thông tin bài làm
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2}>
              <Chip
                icon={<TimerIcon />}
                label={`Bắt đầu: ${format(new Date(attempt?.started_at || new Date()), 'dd/MM/yyyy HH:mm', { locale: vi })}`}
                variant="outlined"
              />
              {attempt?.submitted_at && (
                <Chip
                  icon={<TimerIcon />}
                  label={`Hoàn thành: ${format(new Date(attempt.submitted_at), 'dd/MM/yyyy HH:mm', { locale: vi })}`}
                  variant="outlined"
                />
              )}
              {attempt?.user_info?.name && (
                <Chip
                  label={`Người làm: ${attempt.user_info.name}`}
                  variant="outlined"
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="center" gap={2} mb={3}>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
          >
            Chia sẻ kết quả
          </Button>

          {quiz?.max_attempts === null || (quiz?.attempts_count || 0) < quiz?.max_attempts ? (
            <Button
              variant="contained"
              startIcon={<RetryIcon />}
              onClick={handleRetry}
            >
              Làm lại
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<RetryIcon />}
              disabled
              title="Đã hết lượt làm bài"
            >
              Làm lại
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/quizzes')}
          >
            Danh sách bài tập
          </Button>
        </Box>

        {/* Questions Review */}
        {quiz?.show_correct_answers && questions && questions.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Chi tiết từng câu hỏi
              </Typography>

              {questions?.map((questionResult, index) => {
                // Skip if question data is missing
                if (!questionResult?.question) {
                  return null;
                }

                const isAnswered = questionResult.answered;
                const isCorrect = questionResult.is_correct;

                return (
                  <Box key={questionResult.question_id} sx={{ mb: 3, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      {!isAnswered ? (
                        <Box sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: 'grey.400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" color="white">
                            ?
                          </Typography>
                        </Box>
                      ) : isCorrect ? (
                        <CorrectIcon color="success" />
                      ) : (
                        <WrongIcon color="error" />
                      )}

                      <Box flex={1}>
                        <Typography variant="subtitle1" component="div">
                          Câu {index + 1}:
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <ContentRenderer text={questionResult.question.text || 'Không có câu hỏi'} display="block" />
                        </Box>
                      </Box>

                      <Chip
                        label={!isAnswered ? 'Chưa trả lời' : (isCorrect ? 'Đúng' : 'Sai')}
                        color={!isAnswered ? 'default' : (isCorrect ? 'success' : 'error')}
                        size="small"
                      />
                    </Box>

                    <Box ml={4}>
                      {(questionResult.question.answer_options || []).map((answerOption, displayIndex) => {
                        // Use answer objects with IDs for better handling
                        const correctAnswerId = questionResult.correct_answer_id;
                        const selectedAnswerId = questionResult.selected_answer_id;

                        const isCorrect = correctAnswerId === answerOption.id;
                        const isSelected = selectedAnswerId === answerOption.id;
                        const isWrong = isSelected && !isCorrect;

                        let color = 'text.primary';
                        let backgroundColor = 'transparent';

                        if (isCorrect) {
                          color = 'success.main';
                          backgroundColor = 'success.light';
                        } else if (isSelected && !isCorrect) {
                          color = 'error.main';
                          backgroundColor = 'error.light';
                        }

                        return (
                          <Box
                            key={answerOption.id}
                            sx={{
                              p: 1,
                              mb: 1,
                              borderRadius: 1,
                              backgroundColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              opacity: !isAnswered ? 0.7 : 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Typography variant="body2" color={color}>
                                {String.fromCharCode(65 + displayIndex)}.
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                <ContentRenderer text={answerOption.text || 'Không có đáp án'} display="inline" />
                              </Box>
                            </Box>

                            {isSelected && (
                              <Chip
                                label="Bạn chọn"
                                size="small"
                                color={isCorrect ? 'success' : 'error'}
                                sx={{ ml: 'auto' }}
                              />
                            )}

                            {isCorrect && (
                              <Chip
                                label="Đáp án đúng"
                                size="small"
                                color="success"
                                sx={{ ml: isSelected ? 1 : 'auto' }}
                              />
                            )}
                          </Box>
                        );
                      })}

                      {!isAnswered && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Bạn chưa trả lời câu hỏi này
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default QuizResults;
